import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataContext } from "../../dataContext";
import TransferDetails from "../components/TransferDetails";
import BottomButton from "../components/BottomButton";
import ConfirmTransferModal from "../components/ConfirmTransferModal";
import Select from "react-select";
import useApi from "../../api/api";
import { useTransferApi } from "../../api/transferApi";
import { ROUTE_CONSTANTS } from "../../constants/routeConstants";

// IncomingDetails handles two surfaces in the new RfP model:
//   1. A pending Request for Pay where the caller is the requestee. Sourced
//      from the dedicated `paymentRequests` list (GET /v1/embedded/request/
//      pending). Renders the approval screen with a card/account picker;
//      Approve POSTs to /v1/embedded/request/{id}/approve and Decline POSTs
//      to /v1/embedded/request/{id}/decline.
//   2. Any other transfer (received Succeeded / Failed money movement, etc.).
//      Sourced via the regular history detail endpoint.
const { TRANSFER_PROCESSING } = ROUTE_CONSTANTS;

const IncomingDetails = ({ homePath, branding, dropDownStyle, dropDownTheme }) => {
  const urlParams = useParams();
  const navigate = useNavigate();
  const {
    paymentRequests,
    getPaymentRequests,
    history,
    getHistory,
    accounts,
    cards,
    p2pConfig,
    markRequestLocallyHandled,
  } = useContext(DataContext);
  const api = useApi(false);
  const { getTransferStatus } = useTransferApi();

  const [pending, setPending] = useState(null);
  const [terminal, setTerminal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Confirmation modal — only shown when the FI has it enabled. Same
  // gating + plumbing as SendMoney's confirmation flow.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmitFn, setPendingSubmitFn] = useState(null);

  // The approve screen shows a card OR an account dropdown depending on the
  // FI's rail_type — same behavior SendMoney uses. p2p_config.rail_type comes
  // back lowercase ("card" / "account"). Default to card behavior when
  // unspecified.
  const railUsesCard = p2pConfig?.rail_type !== "account";

  // Pre-select the user's primary card or account so Approve is one tap.
  const [selectedCard, setSelectedCard] = useState(undefined);
  const [selectedAccount, setSelectedAccount] = useState(undefined);

  useEffect(() => {
    if (!selectedCard && cards?.length) {
      setSelectedCard({
        value: cards[0].uuid,
        label: cards[0].display_label,
      });
    }
  }, [cards, selectedCard]);

  // Load the request. If it's in the pending list, that's the source of truth
  // and we render the approval screen. Otherwise fall back to the per-transfer
  // detail endpoint to show terminal status.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const inPending = (paymentRequests || []).find(
        (p) => p.id === urlParams.id
      );
      if (inPending) {
        if (!cancelled) setPending(inPending);
        return;
      }
      // Fall back to history (covers Succeeded / Failed money movements that
      // an incoming-details deeplink might still point at).
      const hist = (history || []).find((t) => t.id === urlParams.id);
      if (hist) {
        if (!cancelled) setTerminal(hist);
        return;
      }
      try {
        const data = await getTransferStatus(urlParams.id);
        if (!cancelled) setTerminal(data);
      } catch (e) {
        // Non-fatal — UI shows "Transfer Not Found".
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.id, paymentRequests, history]);

  // Build account options identical to SendMoney's "From Account" dropdown
  // (masked DDA last 4) so the UI matches.
  const accountOptions = useMemo(
    () =>
      (accounts || []).map((a) => {
        const accountNumber = a.dda || "";
        const cleanAccountNumber = accountNumber.includes("_")
          ? accountNumber.split("_").pop()
          : accountNumber;
        const masked =
          cleanAccountNumber.length > 4
            ? `****${cleanAccountNumber.slice(-4)}`
            : cleanAccountNumber;
        return {
          value: a.id,
          label: `${a.code === "CK" ? "Checking" : "Savings"} ${masked}`,
        };
      }),
    [accounts]
  );

  // Default the account dropdown to the caller's primary (Checking preferred).
  useEffect(() => {
    if (!selectedAccount && accountOptions.length > 0) {
      // accounts already sorted in dataContext (Checking first), so options[0] is fine.
      setSelectedAccount(accountOptions[0]);
    }
  }, [accountOptions, selectedAccount]);

  const cardOptions = useMemo(
    () =>
      (cards || []).map((c) => ({
        value: c.uuid,
        label: c.display_label || `Card Ending ${c.last_four}`,
      })),
    [cards]
  );

  const flashError = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(""), 4000);
  };

  const buildApprovalSummary = () => ({
    recipient:
      pending?.sender_identifier || pending?.sender_name || "the requester",
    amount: pending?.amount?.value
      ? parseFloat(pending.amount.value).toFixed(2)
      : "",
    note: pending?.note?.trim() || "",
    fromLabel: selectedAccount?.label || "",
    isScheduled: false,
    scheduledDate: null,
    repeatFrequency: null,
  });

  const handleApprove = async () => {
    if (!pending?.id) return;

    // Derive the funding account + card to send. For card-rail FIs we need
    // the card's PARENT account id (cards belong to accounts) — same
    // resolution SendMoney does. For account-rail FIs we use the selected
    // account directly and don't send a card_uuid.
    let accountIdToSend = null;
    let cardUuidToSend = null;
    if (railUsesCard) {
      const cardUuid = selectedCard?.value || cards?.[0]?.uuid;
      if (!cardUuid) {
        flashError("Pick a card to pay with.");
        return;
      }
      cardUuidToSend = cardUuid;
      const parent = (accounts || []).find((acc) =>
        (acc.card_uuids || []).includes(cardUuid)
      );
      if (!parent) {
        flashError("Couldn't find an account for that card.");
        return;
      }
      accountIdToSend = parent.id;
    } else {
      if (!selectedAccount?.value) {
        flashError("Pick a funding account.");
        return;
      }
      accountIdToSend = selectedAccount.value;
    }

    // The actual approve POST — same flow regardless of whether we go
    // through the confirmation modal first. Drops the request from local
    // pending state instantly (pub/sub will catch up) and routes to the
    // standard TransferProcessing → TransferSuccessful flow used by sends.
    const executeApprove = async () => {
      await api.post(`request/${pending.id}/approve`, {
        account: accountIdToSend,
        card_uuid: cardUuidToSend,
      });
      if (markRequestLocallyHandled) {
        markRequestLocallyHandled(pending.id);
      }
      setTimeout(() => {
        getPaymentRequests();
        getHistory();
      }, 1200);
      navigate(`${TRANSFER_PROCESSING.replace(":id", pending.id)}`);
    };

    if (branding?.show_confirmation_page) {
      setPendingSubmitFn(() => executeApprove);
      setShowConfirmModal(true);
      return;
    }

    setSubmitting(true);
    try {
      await executeApprove();
    } catch (e) {
      flashError(
        e?.response?.data?.detail || "Could not approve this request."
      );
      setSubmitting(false);
    }
  };

  const handleConfirmModalConfirm = async () => {
    setShowConfirmModal(false);
    if (!pendingSubmitFn) return;
    setSubmitting(true);
    try {
      await pendingSubmitFn();
    } catch (e) {
      flashError(
        e?.response?.data?.detail || "Could not approve this request."
      );
      setSubmitting(false);
    } finally {
      setPendingSubmitFn(null);
    }
  };

  const handleConfirmModalCancel = () => {
    setShowConfirmModal(false);
    setPendingSubmitFn(null);
  };

  const handleDecline = async () => {
    if (!pending?.id) return;
    setSubmitting(true);
    try {
      await api.post(`request/${pending.id}/decline`);
      // Optimistically drop from local state so the badge clears immediately;
      // backend pub/sub will catch up to RequestDeclined async.
      if (markRequestLocallyHandled) {
        markRequestLocallyHandled(pending.id);
      }
      getPaymentRequests();
      navigate(homePath);
    } catch (e) {
      flashError(
        e?.response?.data?.detail || "Could not decline this request."
      );
      setSubmitting(false);
    }
  };

  const renderPending = () => (
    <div style={{ padding: "0 4px" }}>
      {/* Header — who's asking and how much. Centered, plain background so it
          doesn't compete with the action buttons below. */}
      <div style={{ textAlign: "center", padding: "24px 0 12px" }}>
        <div
          style={{
            fontSize: "13px",
            color: "var(--sending_text_secondary)",
            marginBottom: "6px",
          }}
        >
          {pending.sender_identifier || pending.sender_name || "Someone"} is
          requesting
        </div>
        <div
          style={{
            fontSize: "40px",
            fontWeight: 600,
            lineHeight: 1.1,
            color: "var(--sending_text_primary)",
          }}
        >
          ${parseFloat(pending.amount?.value).toFixed(2)}
        </div>
        {pending.note?.trim() && (
          <div
            style={{
              fontSize: "13px",
              color: "var(--sending_text_secondary)",
              marginTop: "10px",
              fontStyle: "italic",
            }}
          >
            "{pending.note}"
          </div>
        )}
      </div>

      {/* Mirror SendMoney's behavior: card-rail FIs pick a card; account-rail
          FIs pick an account. */}
      <div style={{ marginTop: "8px" }}>
        {railUsesCard ? (
          <>
            <label
              htmlFor="approve-card"
              style={{ fontSize: "13px", fontWeight: 500 }}
            >
              From Card
            </label>
            <Select
              inputId="approve-card"
              options={cardOptions}
              value={selectedCard}
              onChange={(opt) => setSelectedCard(opt)}
              isDisabled={submitting}
              styles={dropDownStyle}
              theme={dropDownTheme}
            />
          </>
        ) : (
          <>
            <label
              htmlFor="approve-account"
              style={{ fontSize: "13px", fontWeight: 500 }}
            >
              From Account
            </label>
            <Select
              inputId="approve-account"
              options={accountOptions}
              value={selectedAccount}
              onChange={(opt) => setSelectedAccount(opt)}
              isDisabled={submitting}
              styles={dropDownStyle}
              theme={dropDownTheme}
            />
          </>
        )}
      </div>

      {errorMessage && (
        <div
          style={{
            color: "var(--sending_alert_color)",
            marginTop: "14px",
            fontSize: "13px",
            textAlign: "center",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "10px",
          marginTop: "28px",
          flexDirection: "column",
        }}
      >
        <button
          type="button"
          onClick={handleApprove}
          disabled={submitting}
          data-cy="approve_request_button"
        >
          {submitting
            ? "Working…"
            : `Pay $${parseFloat(pending.amount?.value).toFixed(2)}`}
        </button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={submitting}
          style={{
            backgroundColor: "#ffffff",
            color: "var(--sending_alert_color)",
            border: "1px solid #000000",
          }}
          data-cy="decline_request_button"
        >
          Decline Request
        </button>
      </div>
    </div>
  );

  const renderTerminal = () => {
    const status = terminal.status || terminal.transfer_state;
    const heading =
      status === "DECLINED"
        ? "You declined this request"
        : status === "EXPIRED"
          ? "This request expired"
          : status === "SUCCEEDED"
            ? "Payment received"
            : status === "FAILED"
              ? "Request failed"
              : "Payment received";
    return (
      <div>
        <h3 className="status">{heading}</h3>
        <hr style={{ margin: "20px 0" }} />
        {TransferDetails(terminal)}
      </div>
    );
  };

  return (
    <>
      <main className="page_with_nav">
        {pending
          ? renderPending()
          : terminal
            ? renderTerminal()
            : (
              <div className="center_page">Transfer Not Found</div>
            )}
        {BottomButton(() => navigate(homePath), "Return Home")}
      </main>
      {showConfirmModal && pending && (
        <ConfirmTransferModal
          transferSummary={buildApprovalSummary()}
          onConfirm={handleConfirmModalConfirm}
          onCancel={handleConfirmModalCancel}
        />
      )}
    </>
  );
};

export default IncomingDetails;
